import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	GOOGLE_DRIVE_PROVIDER_KEY,
	useSmartlinkConnectionLinkCount,
} from '../../../banners/hooks/useSmartlinkConnectionLinkCount.ts';
import { useConnectorOrgAuthRates } from '../../../banners/hooks/useConnectorOrgAuthRates.ts';
import { useOrgConnectedAppsRates } from '../../../banners/hooks/useOrgConnectedAppsRates.ts';
import { useGdriveSmartLinkAuth } from '../../../footer/hooks/useGdriveSmartLinkAuth.ts';
import { hasOnlyFirstPartyLinks } from '../../../banners/utils/connectorEligibility.ts';
import { toProductKeyRates } from '../../../utils/connectorProviders.ts';
import type { EnhancedSmartLink } from '../../../utils/smartLinkTypes.ts';
import {
	collectUnconnectedTraitKeys,
	isUnconnectedThirdParty,
	pickTopUnconnectedApp,
} from '../../../utils/unconnectedThirdPartyApps.ts';
import type { ConnectorPromoBannerModel } from '../connectorPromoBannerModel.ts';
import { selectConnectorPromoBanner } from '../utils/selectConnectorPromoBanner.ts';
import {
	getBannerClickedEvent,
	getBannerNotShownEvent,
	getBannerShownEvent,
	getBannerShownAttributes,
} from '../promoBannerAnalytics.ts';
import { useTwgAnalyticsEvents } from '../../../useTwgAnalyticsEvents.ts';

import { useConnectableApps } from './useConnectableApps.ts';

export type UseConnectorPromoBannerInput = {
	/** Passed in, not fetched: the modal already collects these for "Links to". */
	links: EnhancedSmartLink[];
};

export type UseConnectorPromoBannerResult = ConnectorPromoBannerModel | null;

/**
 * Returns banner data and actions without rendering. The panel calls this hook once and shares
 * the resulting banner element with its modal, so trait reads and once-per-mount analytics
 * have one owner across both surfaces.
 *
 * Returns `null` without Rovo — where the dialog the banner opens is no use — and while
 * the deciding traits load, so the copy never changes under the user.
 */
export const useConnectorPromoBanner = ({
	links,
}: UseConnectorPromoBannerInput): UseConnectorPromoBannerResult => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const { cloudId, isRovoLLMEnabled } = useSessionData();

	const isPageFirstPartyOnly = hasOnlyFirstPartyLinks(links);

	const { value: linkCounts, loading: isLinkCountLoading } = useSmartlinkConnectionLinkCount({
		skip: !isPageFirstPartyOnly,
	});
	const hasSiteWideDriveLinks = (linkCounts?.[GOOGLE_DRIVE_PROVIDER_KEY] ?? 0) >= 1;

	const { isAuthenticated: isGdriveConnected, isLoading: isGdriveAuthLoading } =
		useGdriveSmartLinkAuth();

	const unconnectedThirdPartyLinks = useMemo(() => links.filter(isUnconnectedThirdParty), [links]);
	const hasUnconnectedThirdParty = unconnectedThirdPartyLinks.length > 0;

	const { value: orgRates, loading: isRatesLoading } = useOrgConnectedAppsRates({
		skip: !hasUnconnectedThirdParty,
	});
	const topApp = useMemo(
		() => pickTopUnconnectedApp(orgRates, collectUnconnectedTraitKeys(unconnectedThirdPartyLinks)),
		[orgRates, unconnectedThirdPartyLinks],
	);

	// The trait hooks share one store with a global `loading` flag, so each is only
	// honoured when its branch is in play.
	const isLoading =
		(isPageFirstPartyOnly && (isLinkCountLoading || isGdriveAuthLoading)) ||
		(hasUnconnectedThirdParty && isRatesLoading);

	const selection = useMemo(
		() =>
			selectConnectorPromoBanner({
				isPageFirstPartyOnly,
				hasSiteWideDriveLinks,
				isGdriveConnected,
				hasUnconnectedThirdParty,
				topApp,
			}),
		[
			isPageFirstPartyOnly,
			hasSiteWideDriveLinks,
			isGdriveConnected,
			hasUnconnectedThirdParty,
			topApp,
		],
	);
	const { variant } = selection;

	const { value: connectorAuthRates } = useConnectorOrgAuthRates();

	// The org variant ranks its icons by the same trait its copy quotes, so the two agree.
	// The other variants quote no adoption figure, so they fall back to the Rovo connector
	// trait — the same one the CWR ghost icons rank on.
	const iconRates = useMemo(
		() => (variant === 'orgConnectedApps' ? toProductKeyRates(orgRates) : connectorAuthRates),
		[variant, orgRates, connectorAuthRates],
	);

	// The Drive variant names Google Drive, so its icons lead with Drive.
	const { apps } = useConnectableApps({
		pinnedProductKey: variant === 'gdrive' ? ProductKeys3P.GoogleDrive : undefined,
		rates: iconRates,
	});

	const shownAttributes = useMemo(
		() => getBannerShownAttributes(selection, apps.length),
		[selection, apps.length],
	);

	// Once per mount, on the variant first seen. Exactly one of `viewed` and `notShown`
	// fires, so `clicked / viewed` stays a click-through rate.
	const hasFiredRef = useRef(false);
	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		if (!isRovoLLMEnabled) {
			hasFiredRef.current = true;
			createAnalyticsEvent(getBannerNotShownEvent({ reason: 'rovoDisabled' })).fire();
			return;
		}
		if (isLoading) {
			return;
		}
		hasFiredRef.current = true;
		createAnalyticsEvent(getBannerShownEvent(shownAttributes)).fire();
	}, [isRovoLLMEnabled, isLoading, shownAttributes, createAnalyticsEvent]);

	const fireClickedEvent = useCallback(() => {
		createAnalyticsEvent(getBannerClickedEvent(shownAttributes)).fire();
	}, [createAnalyticsEvent, shownAttributes]);

	if (!isRovoLLMEnabled || isLoading) {
		return null;
	}

	return { cloudId, selection, apps, onClick: fireClickedEvent };
};
